import React, { memo } from "react";
import { useImageUpload } from "../hooks/useImageUpload";

const ImageUpload = memo(({
  title,
  description,
  inputId,
  accept = "image/*",
  required = false,
  onImageChange = () => {},
}) => {
  const {
    image,
    preview,
    loading,
    error,
    isDragOver,
    handleFileUpload,
    removeImage,
    clearError,
    dragProps,
  } = useImageUpload(inputId);

  // Notify parent when image changes - memoized
  const notifyImageChange = React.useCallback((image) => {
    if (image !== null) {
      onImageChange(image);
    }
  }, [onImageChange]);

  React.useEffect(() => {
    notifyImageChange(image);
  }, [image, notifyImageChange]);

  return (
    <div
      className={`rounded-lg border-2 border-dashed transition-all duration-300 ${
        isDragOver
          ? "bg-blue-100 border-blue-500 scale-105 shadow-lg"
          : "bg-gray-50 border-gray-200 hover:border-blue-400 hover:bg-blue-50"
      }`}
      {...dragProps}
    >
      {title && (
        <>
          <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
            <span className="mr-2">📸</span>
            {title}
          </h3>
          <p className="text-gray-600 mb-4 text-sm leading-relaxed">
            {description} You can also drag and drop an image here.
          </p>
        </>
      )}

      {/* Drag and Drop Indicator */}
      {isDragOver && !preview && (
        <div className="p-6">
          <div className="border-2 border-dashed border-blue-400 rounded-lg p-8 text-center bg-blue-50">
            <div className="text-blue-600 font-semibold text-lg mb-2">
              📁 Drop your image here
            </div>
            <div className="text-blue-500 text-sm">
              Release to upload the image
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-6">
          <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm">{error}</span>
              <button
                onClick={clearError}
                className="text-red-500 hover:text-red-700 font-bold"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="p-6">
          <div className="p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded-lg">
            <span className="text-sm">⏳ Processing image...</span>
          </div>
        </div>
      )}

      {/* Image Preview */}
      {preview ? (
        <div className="p-6">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-white relative">
            <img
              src={preview}
              alt={`${title} preview`}
              className="w-full max-w-md mx-auto rounded-lg shadow-md"
            />

            {/* Remove Button */}
            <button
              onClick={removeImage}
              className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold transition-colors duration-200 shadow-lg"
              title="Remove image"
            >
              ×
            </button>

            {/* File Info */}
            <div className="text-center mt-2 text-sm text-gray-600">
              ✅ {image?.name}
            </div>

            {/* Change Photo Button */}
            <div className="text-center mt-2">
              <input
                type="file"
                accept={accept}
                onChange={handleFileUpload}
                className="hidden"
                id={`${inputId}-change`}
              />
              <label
                htmlFor={`${inputId}-change`}
                className="inline-block bg-green-600 text-white px-4 py-2 rounded-full cursor-pointer text-sm font-medium transition-all duration-300 hover:bg-green-700 hover:shadow-lg hover:-translate-y-1"
              >
                📁 Change Photo
              </label>
            </div>
          </div>
        </div>
      ) : (
        /* File Upload Button */
        <div className="p-6">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-white hover:border-blue-400 hover:bg-blue-50 transition-all duration-300">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="text-gray-600 mb-4">
              <p className="text-sm">Click to select a photo or drag and drop</p>
            </div>
            <input
              type="file"
              accept={accept}
              onChange={handleFileUpload}
              className="hidden"
              id={inputId}
              required={required}
            />
            <label
              htmlFor={inputId}
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg cursor-pointer font-medium transition-all duration-300 hover:bg-blue-700 hover:shadow-lg hover:-translate-y-1"
            >
              📷 Select Photo
            </label>
          </div>
        </div>
      )}
    </div>
  );
});

ImageUpload.displayName = 'ImageUpload';

export default ImageUpload;
